import { useEffect, useState } from 'react';
import Modal from '../../../components/organisms/modal/Modal';
import ColorEnum from '../../../enums/colorEnum';
import SendMessageModalEventManager, { SendMessageEventData, SendMessageModalCustomEventType } from '../modal/SendMessageModalEventManager';
import Text from '../../atoms/typography/Text';
import { FontWeightEnum } from '../../enums';
import { useTranslation } from 'react-i18next';
import styles from './SendMessageModal.module.css';
import findHomeSeekerApplicationById, { HomeseekerApplication } from '../../../api/homeseeker-application/findHomeSeekerApplicationById';
import Label from '../../atoms/label/Label';
import TextInput from '../../../components/forms/textInput/TextInput';
import { TextArea } from '../../atoms/text-area';
import ButtonNew from '../../molecules/button/Button';
import MailSendLineIcon from '../../atoms/icons/MailSendLineIcon';
import Form from '../../molecules/form/Form';
import sendManualEmails from '../../../api/agency/sendManualEmails';
import SkeletonComponent from '../../../components/SkeletonComponent';
import Notifications from '../notifications/Notifications';

interface SendMessageModalProps {
    readonly open: boolean;
}

const sendMessageModal = SendMessageModalEventManager.create<SendMessageEventData>('send-message-modal');
type SendMessageModalComponent = (props: SendMessageModalProps) => JSX.Element;

const SendMessageModal = ({
    open = false,
}: SendMessageModalProps) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(open);
    const [homeseekerApplicationId, setHomeseekerApplicationId] = useState('');
    const [homeseeker, setHomeseeker] = useState<HomeseekerApplication>(null);
    const [isFetchingHomeseekerApplication, setIsFetchingHomeseekerApplication] = useState(true);
    const [message, setMessage] = useState('');
    const [isFormValid, setIsFormValid] = useState({});
    const [, setFormErrors] = useState({});
    const [formData, setFormData] = useState({});

    const setModal = (e: SendMessageModalCustomEventType) => {
        setIsOpen(e.detail.open);
        setHomeseekerApplicationId(e.detail.homeseekerApplicationId);
    }

    const fetchHomeseekerApplicationById = async () => {
        setIsFetchingHomeseekerApplication(true);
        try {
            const response = await findHomeSeekerApplicationById(homeseekerApplicationId);
            setHomeseeker(response?.result);
        } catch (e) {
            Notifications.add({
                message: t('candidate.notes.createNote.error.title'),
                type: 'failed'
            });
        } finally {
            setIsFetchingHomeseekerApplication(false);
        }
    }

    useEffect(() => {
        if (homeseekerApplicationId) {
            fetchHomeseekerApplicationById();
        }

    }, [homeseekerApplicationId])


    useEffect(() => {
        sendMessageModal?.addListener(setModal);

        return () => {
            sendMessageModal?.removeListener(setModal);
        };
    }, []);

    const handleFormChange = (data, isValid, errors) => {
        setFormData(data);
        setIsFormValid(isValid);
        setFormErrors(errors);
    }

    const handleSubmit = () => {
        try {
            sendManualEmails({
                homeseekerApplicationIds: [
                    homeseekerApplicationId
                ],
                subject: formData['subject']?.value,
                message: message
            });

            Notifications.add({
                type: 'success',
                message: 'Bericht verzonden.',
            });
        } catch (e) {
            Notifications.add({
                message: t('candidate.notes.createNote.error.title'),
                type: 'failed'
            });
        } finally {
            sendMessageModal.close();
        }
    }

    const skeletonComponents = () => {
        return (
            <div className={styles.messageForm}>
                <SkeletonComponent width={170} height={20} />
                <div>
                    <SkeletonComponent width={60} height={20} />
                    <SkeletonComponent width={400} height={20} />
                </div>
                <div>
                    <SkeletonComponent width={90} height={20} />
                    <SkeletonComponent width={610} height={40} />
                </div>
                <div>
                    <SkeletonComponent width={70} height={20} />
                    <SkeletonComponent width={610} height={260} />
                </div>
                <div className={styles.sendMessageButton}>
                    <SkeletonComponent width={140} height={40} />
                </div>
            </div>
        );
    }

    return (
        <Modal
            icon={'ri-mail-line'}
            iconColor={ColorEnum.Primary600}
            iconBackgroundColor={ColorEnum.Primary100}
            flowyColor={ColorEnum.Primary100}
            manager={sendMessageModal}
            isOpen={isOpen}
        >
            {
                (isFetchingHomeseekerApplication) ? skeletonComponents() :
                    <Form
                        onChange={handleFormChange}
                        onSubmit={handleSubmit}
                        className={styles.messageForm}
                    >
                        <Text
                            weight={FontWeightEnum.Bold}
                            className={styles.heading}
                        >
                            {t('candidate.action.sendMessage.title')}
                        </Text>
                        <Text
                            className={styles.receiver}
                        >
                            {t('candidate.action.sendMessage.to')}
                            <Text
                                weight={FontWeightEnum.Semibold}
                            >
                                {(homeseeker) ? homeseeker.email : ''}
                            </Text>
                        </Text>
                        <div>
                            <Label
                                required
                                fontWeight={FontWeightEnum.Semibold}
                            >
                                {t('candidate.action.sendMessage.subject')}
                            </Label>
                            <TextInput name='subject' placeholder={t('candidate.action.sendMessage.subject')} value={formData['subject']?.value} required />
                        </div>
                        <div>
                            <Label
                                fontWeight={FontWeightEnum.Semibold}
                            >
                                {t('candidate.action.sendMessage.message')}
                            </Label>
                            <TextArea id='textarea' onChange={(name, value) => setMessage(value)} />
                        </div>
                        <ButtonNew
                            iconBefore={<MailSendLineIcon />}
                            className={styles.sendMessageButton}
                            type='submit'
                            disabled={!isFormValid}
                        >
                            {t('candidate.action.sendMessage')}
                        </ButtonNew>
                    </Form>
            }
        </Modal>
    );
}


Object.assign(SendMessageModal, sendMessageModal);

export default (SendMessageModal as SendMessageModalComponent & SendMessageModalEventManager<SendMessageEventData>);
